import { ref, readonly, computed } from "vue";

/**
 * 响应式断点类型
 * - desktop：桌面端布局（默认，宽度 > MOBILE_MAX_WIDTH）
 * - mobile：移动端布局（宽度 <= MOBILE_MAX_WIDTH）
 */
export type Breakpoint = "desktop" | "mobile";

/** 移动端最大宽度阈值（px），与 ForceHiddenSidebarButton 中的 800 保持一致 */
export const MOBILE_MAX_WIDTH = 800;

// 全局共享一个 MediaQueryList 实例与响应式状态
// 所有使用 useBreakpoint 的组件共享同一份检测结果，避免重复监听
const current = ref<Breakpoint>(
  typeof window !== "undefined" &&
    window.matchMedia(`(max-width: ${MOBILE_MAX_WIDTH}px)`).matches
    ? "mobile"
    : "desktop"
);

let mql: MediaQueryList | null = null;

const handleChange = (e: MediaQueryListEvent) => {
  current.value = e.matches ? "mobile" : "desktop";
};

/**
 * 全局共享的断点检测 composable
 *
 * 设计要点：
 * - 单一 MediaQueryList 实例，多组件复用，零额外开销
 * - 仅在 SSR 场景下做 typeof window 守卫（本项目当前是 CSR）
 * - 暴露只读响应式 ref / computed，组件可直接在模板中使用
 *
 * 推荐用法：
 *   const { isMobile } = useBreakpoint();
 *   // 模板中 v-if="isMobile"
 */
export function useBreakpoint() {
  // 首次调用时初始化监听
  if (typeof window !== "undefined" && !mql) {
    mql = window.matchMedia(`(max-width: ${MOBILE_MAX_WIDTH}px)`);
    current.value = mql.matches ? "mobile" : "desktop";
    mql.addEventListener("change", handleChange);
  }

  // isMobile 用 computed 保证与 current 同步
  const isMobile = computed(() => current.value === "mobile");
  const isDesktop = computed(() => current.value === "desktop");

  return {
    /** 当前断点（只读响应式） */
    breakpoint: readonly(current),
    /** 是否移动端（只读 computed） */
    isMobile,
    /** 是否桌面端（只读 computed） */
    isDesktop,
  };
}
